import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getRequest } from "../../../utils/api";

export interface MetaPlan {
  _id: string;
  ppm_plan_title: string;
  ppm_plan_is_black_card?: boolean;
  ppm_markup_marketing?: number;
  ppm_markup_utility?: number;
  ppm_markup_authentication?: number;
  ppm_markup_service?: number;
}

export interface CountryOption {
  cmp_country_code: string;
  cmp_country_name: string;
}

export interface BasePricing {
  cmp_country_code: string;
  cmp_country_name: string;
  cmp_meta_marketing_price: number;
  cmp_meta_utility_price: number;
  cmp_meta_authentication_price: number;
  cmp_meta_service_price: number;
}

export type CatId = "marketing" | "utility" | "authentication" | "service";
export type Markups = Record<string, Record<CatId, number>>;

export const CATS: { id: CatId; name: string; desc: string; key: keyof BasePricing }[] = [
  { id: "marketing", name: "Marketing", desc: "Promotions & offers", key: "cmp_meta_marketing_price" },
  { id: "utility", name: "Utility", desc: "Order & account updates", key: "cmp_meta_utility_price" },
  { id: "authentication", name: "Authentication", desc: "OTP & verification", key: "cmp_meta_authentication_price" },
  { id: "service", name: "Service", desc: "Free-form replies in the 24h window", key: "cmp_meta_service_price" },
];

export const emptyMarkup = (): Record<CatId, number> => ({ marketing: 0, utility: 0, authentication: 0, service: 0 });

// Plan markups are uniform across every country (one % per plan per category) — only the Meta
// base rate varies by country, so switching countries never needs to refetch markups, only
// basePricing. Countries are fetched once; base pricing + plans are refetched per country.
const useMetaPricing = () => {
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [countryCode, setCountryCode] = useState<string>("IN");
  const [plans, setPlans] = useState<MetaPlan[]>([]);
  const [basePricing, setBasePricing] = useState<BasePricing | null>(null);
  const [markups, setMarkups] = useState<Markups>({});

  // Fetched once — the dropdown's option list
  useEffect(() => {
    (async () => {
      try {
        const response: any = await getRequest(`/plans/countries`);
        if (response.status === "success") setCountries(response.data || []);
      } catch (error) {
        toast.error("Failed to load country list.");
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response: any = await getRequest(`/plans/get/${countryCode}`);
      if (response.status === "success") {
        setBasePricing(response.data.basePricing);
        setPlans(response.data.plans);
        const initial: Markups = {};
        response.data.plans.forEach((p: MetaPlan) => {
          initial[p._id] = {
            marketing: p.ppm_markup_marketing || 0,
            utility: p.ppm_markup_utility || 0,
            authentication: p.ppm_markup_authentication || 0,
            service: p.ppm_markup_service || 0,
          };
        });
        setMarkups(initial);
      }
    } catch (error) {
      toast.error("Failed to load Meta pricing data.");
    } finally {
      setLoading(false);
    }
  }, [countryCode]);

  useEffect(() => { load(); }, [load]);

  const base = (key: keyof BasePricing) => (basePricing ? Number(basePricing[key]) || 0 : 0);

  return { loading, setLoading, countries, countryCode, setCountryCode, plans, basePricing, base, markups, setMarkups, reload: load };
};

export default useMetaPricing;
